import { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Globe } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resumeUpload } from "../../../supports/resumeUpload";
import { useToast } from '../../../common/toastSystem';
import translations from '../../../assets/json/translations.json';

export default function ConsentPage() {
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState('EN');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const Key = searchParams.get('key');
  const cContext = searchParams.get('c_context') === 'True';

  const languages = [
    { code: 'ARB', name: 'Arabic' },
    { code: 'BEN', name: 'Bengali' },
    { code: 'BUR', name: 'Burmese' },
    { code: 'CAN', name: 'Cantonese' },
    { code: 'DU', name: 'Dutch' },
    { code: 'EN', name: 'English' },
    { code: 'FR', name: 'French' },
    { code: 'GE', name: 'German' }
  ];

  const t = translations[language] || translations.EN;

  const handleDelete = async () => {
    setLoading(true);  
    try {
      let requestBody = {
        token: Key,
        consent: true
      }
      const response = await resumeUpload.userConsent(requestBody);
      
      if (response.status_code === 200) {
        setStatus('deleted');
        setLoading(false);
      } else {
        toast.warning(response.message || 'Error deleting account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  const handleKeep = async () => {
    setLoading(true);
    try {
      let requestBody = {
        token: Key,
        consent: false
      }
      const response = await resumeUpload.userConsent(requestBody);
      if (response.status_code === 200) {
        setStatus('kept');
        setLoading(false);
      } else {
        toast.warning(response.message || 'Error keeping account');
      }
    } catch (error) {
      console.error('Error keeping account:', error);
    } 
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      {/* Language Dropdown */}
      {/* <div className="absolute top-4 right-4">
        <div className="relative">
          <button
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Globe className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              {languages.find(l => l.code === language)?.name} ({language})
            </span>
          </button>

          {showLanguageDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setShowLanguageDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    language === lang.code ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-700'
                  }`}
                >
                  {lang.name} ({lang.code})
                </button>
              ))}
            </div>
          )}
        </div>
      </div> */}

     <div
  className={`bg-white shadow-lg rounded w-full p-8 ${
    cContext ? "max-w-[40rem]" : "max-w-[30rem]"
  }`}
>
        
        {status === 'pending' && (
          <>
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="w-12 h-12 text-red-400" />
            </div>
            
            <h1 className="text-xl font-bold text-gray-900 text-center mb-4">
              {t.title}
            </h1>
            {cContext?(
               <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-xs text-gray-700 mb-4 leading-relaxed">
              With over 40 years of experience, MPH is a trusted provider of international technical recruitment services. Headquartered in Dubai, we connect skilled professionals with impactful roles across MENA, Africa, Europe, and Asia. We specialize in placing talent in key industries such as Oil & Gas, Nuclear, Renewable Energy, Mining, Waste & Water Management, and Aeronautics—covering every phase of the project lifecycle from design to long-term maintenance. Join us to contribute to global infrastructure and energy transition projects. With MPH, you're not just taking on a new role—you're becoming a recognized expert in your field
              </p>

                <p className="text-xs text-gray-700 mb-6 leading-relaxed">
                  We recently sourced your résumé from a job portal, and to retain it for consideration in future career opportunities, we require your consent. In line with our data retention policies and GDPR requirements, we kindly request your approval to store your information in our system.
                </p>
            </div>
            ):(
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-gray-600 text-xs leading-relaxed">
                {t.description}
              </p>
            </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleKeep}
                disabled={loading}
                className="flex-1 text-xs bg-indigo-500 hover:bg-indigo-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading && status === 'keeping' ? (
                  <span>{t.processing}</span>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t.keepButton}
                  </>
                )}
              </button>
              
              <button
                onClick={handleDelete}
                disabled={loading}
                className="flex-1 text-xs bg-red-500 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading && status === 'deleting' ? (
                  <span>{t.processing}</span>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    {t.deleteButton}
                  </>
                )}
              </button>
            </div>

            <p className="text-[0.68rem] text-gray-400 text-center mt-6 tracking-tight">
              {t.noResponseNote}
            </p>
          </>
        )}

        {status === 'kept' && (
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            
            <h1 className="text-xl font-bold text-gray-900 mb-4">
              {t.accountConfirmedTitle}
            </h1>
            
            <p className="text-gray-700 text-sm mb-6">
              {t.accountConfirmedMessage}
            </p>
            
            <div className="bg-green-50 border text-xs border-green-200 rounded-lg p-4 mb-6">
              <p className="text-gray-700">
                {t.accountConfirmedNote}
              </p>
            </div>
            
            <a
              href="/resume-login"
              className="inline-block text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-3 rounded-lg transition-colors"
            >
              {t.goToLogin}
            </a>
          </div>
        )}

        {status === 'deleted' && (
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            
            <h1 className="text-xl font-bold text-gray-900 mb-4">
              {t.accountDeletedTitle}
            </h1>
            
            <p className="text-gray-700 text-sm mb-6">
              {t.accountDeletedMessage}
            </p>
            
            <div className="bg-red-50 border border-red-200 text-xs rounded-lg p-4 mb-6">
              <p className="text-gray-700">
                {t.accountDeletedNote}
              </p>
            </div>
            
            <p className="text-xs text-gray-500">
              {t.thankYou}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}